import { forwardRef, Ref } from 'react'
import { motion } from 'framer-motion'
import { Shard, ShardStatus } from '../types/shard'
import { PersonaIcon } from './PersonaIcon'
import { Worker } from '../hooks/useWorkers'

interface KanbanCardProps {
  shard: Shard
  onClick: (shard: Shard) => void
  workers: Worker[]
  allShards?: Shard[]
}

// Special styling for spec lifecycle states
const SPEC_STATUS_STYLES: Partial<Record<ShardStatus, { border: string; badge: string; badgeText: string }>> = {
  FINAL_REVIEW: {
    border: 'border-purple-500/50',
    badge: 'bg-purple-500/20 text-purple-300',
    badgeText: 'Final Review',
  },
  FINAL_APPROVED: {
    border: 'border-emerald-500/50',
    badge: 'bg-emerald-500/20 text-emerald-300',
    badgeText: 'Complete',
  },
  AWAITING_HUMAN_ACTION: {
    border: 'border-amber-500/50',
    badge: 'bg-amber-500/20 text-amber-300',
    badgeText: 'Awaiting Human',
  },
  // #343: replaced by smaller tasks; in Done, but not a completion.
  DECOMPOSED: {
    border: 'border-gray-600/50',
    badge: 'bg-gray-500/20 text-gray-300',
    badgeText: 'Decomposed',
  },
}

function getTimeInStatus(createdAt: string): string {
  const created = new Date(createdAt)
  const now = new Date()
  const diffMs = now.getTime() - created.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffDays > 0) {
    return `${diffDays}d`
  } else if (diffHours > 0) {
    return `${diffHours}h`
  } else if (diffMins > 0) {
    return `${diffMins}m`
  } else {
    return '0m'
  }
}

function KanbanCardContent({ shard, onClick, workers, allShards, innerRef }: KanbanCardProps & { innerRef?: Ref<HTMLButtonElement> }) {
  const workerName = shard.assigned_worker
  const assigneeName = shard.assignee?.trim() || undefined

  // Try to find worker by assigned_worker name first, then by assignee name
  const worker = workerName
    ? workers.find(w => w.name === workerName)
    : assigneeName
      ? workers.find(w => w.name === assigneeName)
      : null

  // Persona resolution chain:
  // 1. Worker's persona (from workers.yaml lookup)
  // 2. assignee field (always a persona name like "visionary")
  // 3. undefined (falls back to nexus default in PersonaIcon)
  const persona = worker?.persona || assigneeName || undefined
  const timeInStatus = shard.created_at ? getTimeInStatus(shard.created_at) : '-'
  const specStyle = SPEC_STATUS_STYLES[shard.status]

  return (
    <motion.button
      ref={innerRef}
      data-shard-id={shard.id}
      layoutId={shard.id}
      layout="position"
      initial={{ opacity: 0, scale: 0.95, x: -200 }}
      animate={{ opacity: 1, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{
        layout: { type: 'spring', stiffness: 350, damping: 30 },
        opacity: { duration: 0.3 },
        scale: { duration: 0.3 },
        x: { type: 'spring', stiffness: 300, damping: 25 }
      }}
      onClick={() => onClick(shard)}
      className={`w-full text-left p-3 themed-panel border rounded-lg hover:bg-cosmic-surface transition-colors duration-200 group ${
        specStyle ? `${specStyle.border} hover:border-opacity-80` : 'border-cosmic-border hover:border-cosmic-nebula'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-cosmic-star">{shard.id}</span>
          {specStyle && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${specStyle.badge}`}>
              {specStyle.badgeText}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">{timeInStatus}</span>
      </div>

      <h3 className="text-sm font-medium text-white mb-2 line-clamp-2">
        {shard.title}
      </h3>

      {/* Dependencies */}
      {shard.depends_on && shard.depends_on.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {shard.depends_on.map(dep => {
            const depShard = allShards?.find(s => s.id === dep)
            const isDone = depShard && (depShard.status === 'DONE' || depShard.status === 'APPROVED' || depShard.status === 'FINAL_APPROVED')
            return (
              <span key={dep} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                isDone ? 'bg-white/5 text-gray-600 line-through' : 'bg-white/5 text-gray-400'
              }`}>
                {dep}
              </span>
            )
          })}
        </div>
      )}

      <div className="flex items-center gap-2">
        <PersonaIcon persona={persona} size="sm" />
        <span className="text-xs text-gray-400">
          {shard.assigned_worker || 'Unassigned'}
        </span>
      </div>
    </motion.button>
  )
}

export const KanbanCard = forwardRef<HTMLButtonElement, KanbanCardProps>(
  function KanbanCard({ shard, onClick, workers, allShards }, ref) {
    return <KanbanCardContent shard={shard} onClick={onClick} workers={workers} allShards={allShards} innerRef={ref} />
  }
)
